import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import { useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';
import useManageReferralStage from './hooks/useManageReferralStage';

const positiveNumberSchema = z
  .string()
  .min(1, { message: 'common.form.validation.required' })
  .regex(/^\d+$/, { message: 'common.form.validation.invalid' })
  .refine((val) => Number(val) >= 0, { message: 'common.form.validation.invalid' });

const referralStageSchema = z.object({
  stageTitle: z.string().min(1, { message: 'common.form.validation.required' }),
  firstVisitRewardPoint: positiveNumberSchema,
  secondVisitRewardPoint: positiveNumberSchema,
  thirdVisitRewardPoint: positiveNumberSchema,
});

export const ManageReferralStage = ({
  defaultValues,
  stageId,
}: {
  defaultValues: {
    stageTitle: string;
    firstVisitRewardPoint: string;
    secondVisitRewardPoint: string;
    thirdVisitRewardPoint: string;
  };
  stageId: string;
}) => {
  const form = useForm({ schema: referralStageSchema, mode: 'onBlur', defaultValues });
  const { t } = useDynamicLocale();
  const { mutateAsync } = useManageReferralStage();
  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        const payload = {
          stageId,
          ...data,
          totalRewardForAllVisit: '',
        };
        await mutateAsync(payload);
      }}
      fieldsetClassName="space-y-[18px]"
    >
      <Input
        label="pointManagement.pointsConfiguration.manageReferralStage.label.stageTitle"
        placeholder={t('pointManagement.pointsConfiguration.manageReferralStage.placeholder.stageTitle')}
        {...form.register('stageTitle')}
        forceRequired
        disabled
      />
      <Input
        inputMode="numeric"
        type="text"
        onlyNumbers
        label="pointManagement.pointsConfiguration.manageReferralStage.label.firstStageReward"
        placeholder={t('pointManagement.pointsConfiguration.manageReferralStage.placeholder.firstStageReward')}
        maxLength={6}
        {...form.register('firstVisitRewardPoint')}
        forceRequired
      />
      <Input
        inputMode="numeric"
        type="text"
        onlyNumbers
        label="pointManagement.pointsConfiguration.manageReferralStage.label.secondStageReward"
        placeholder={t('pointManagement.pointsConfiguration.manageReferralStage.placeholder.secondStageReward')}
        maxLength={6}
        {...form.register('secondVisitRewardPoint')}
        forceRequired
      />
      <Input
        inputMode="numeric"
        type="text"
        onlyNumbers
        label="pointManagement.pointsConfiguration.manageReferralStage.label.thirdStageReward"
        placeholder={t('pointManagement.pointsConfiguration.manageReferralStage.placeholder.thirdStageReward')}
        maxLength={6}
        {...form.register('thirdVisitRewardPoint')}
        forceRequired
      />
    </Form>
  );
};
